import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  MenuItem,
  Paper,
} from '@mui/material';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import ClientDialogHeader from './ClientWizardHeader';
import ClientOnboardingWizardFooter from './ClientOnboardingWizardFooter';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import ConfirmationDialog from '../../ConfirmationDialog';
import { isEqual } from 'lodash';
import ServiceLevelsForm from './ServiceLevelsForm';

const PREFIX = 'ServiceLevelsWizard';

const classes = {
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  contentPaper: `${PREFIX}-contentPaper`,
  stepContainer: `${PREFIX}-stepContainer`,
  previousButton: `${PREFIX}-previousButton`,
  chipContainer: `${PREFIX}-chipContainer`,
  priceHeader: `${PREFIX}-priceHeader`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    paddingLeft: theme.spacing(18),
    paddingRight: theme.spacing(18),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },

  [`& .${classes.contentPaper}`]: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    borderRadius: 3,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(4),
    height: 'fit-content',
    maxWidth: '760px',
  },

  [`& .${classes.stepContainer}`]: {
    width: '100%',
  },

  [`& .${classes.previousButton}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },

  [`& .${classes.priceHeader}`]: {
    color: theme.palette.lightTextPrimary.main,
    fontWeight: 600,
  },
}));

interface ServiceLevelsWizardProps {
  open: boolean;
  close: () => void;
}

export default function ServiceLevelsWizard({
  open,
  close,
}: ServiceLevelsWizardProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const dialogContentRef = useRef(null);
  const requiredFieldMessage = t('common.requiredField');

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: {
      ServiceLevel: '',
      TransportationAttributes: [] as string[],
      PricingPlan: '',
      Params: [],
    },
  });
  const { control, formState, handleSubmit, reset, getValues, watch, trigger } =
    formHook;

  const client = useAppSelector(clientSlice.selectCurrentClient);
  // TODO: this should load just the client's service levels once we can save them
  const clientServiceLevels = useAppSelector(
    accountingSlice.selectTransportationServices,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const pricingModels = useAppSelector(
    accountingSlice.selectActivePricingModels,
  );

  // TODO: select these once saving is implemented
  const isSaving = false;
  const isSaved = false;

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [steps, setSteps] = useState<string[]>([]);
  const [previousStep, setPreviousStep] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [savedValues, setSavedValues] = useState<{ [step: number]: any }>({});

  useEffect(() => {
    if (clientServiceLevels?.length) {
      setSteps(clientServiceLevels.map(() => ''));
    }
  }, [clientServiceLevels]);

  useEffect(() => {
    if (clientServiceLevels.length > 0) {
      if (previousStep >= 0) {
        const vals = getValues();
        setSavedValues({ ...savedValues, [previousStep]: vals });
      }
      const serviceLevel = clientServiceLevels[activeStep];
      const prevValues = savedValues[activeStep] || {};
      let prevAttributes = prevValues.TransportationAttributes;
      if (!prevAttributes?.length) {
        prevAttributes = serviceLevel.transportationAttributes.map((ta) => {
          return ta.join(':');
        });
      }
      const curValues = {
        ServiceLevel: serviceLevel.transportationServiceUUID,
        PricingPlan: prevValues.PricingPlan || '',
        TransportationAttributes: prevAttributes,
        Params: prevValues.Params || [],
      };
      reset(curValues);
      setPreviousStep(activeStep);
    }
  }, [activeStep, clientServiceLevels, open]);

  const resetForm = () => {
    reset({
      ServiceLevel: '',
      TransportationAttributes: [] as string[],
      PricingPlan: '',
      Params: [],
    });
  };

  const saveData = () => {
    // TODO: save the configured service levels when back end is available.
  };

  const scrollToTop = () => {
    if (dialogContentRef.current) {
      (dialogContentRef.current as any).scrollTop = 0;
    }
  };

  const updateStep = (newStep: number) => {
    if (newStep > steps.length - 1) {
      saveData();
    } else {
      setActiveStep(newStep);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    updateStep(activeStep - 1);
  };

  const handleStep = (step: number) => () => {
    updateStep(step);
  };

  const handleNext = () => {
    updateStep(activeStep + 1);
  };

  const handleClose = () => {
    setConfirmDialogOpen(false);
    setActiveStep(0);
    setSavedValues({});
    resetForm();
    close();
  };

  const formHasChanges = (): boolean => {
    // If we have more than one service level configured in the saved values
    // then the user must have made some changes.
    const keys = Object.keys(savedValues);
    if (keys.length > 1) {
      return true;
    } else if (keys.length === 1) {
      // If there is only one service level configured we just check to see if the
      // the user selected a pricing plan or if they changed any transportation
      // attributes.
      const curValues = getValues();
      const serviceLevel = clientServiceLevels[0];
      const defaultAttributes = serviceLevel.transportationAttributes.map(
        (ta) => {
          return ta.join(':');
        },
      );
      const selectedAttributes = getValues('TransportationAttributes');
      return (
        !!curValues.PricingPlan ||
        !isEqual(defaultAttributes, selectedAttributes)
      );
    }

    return false;
  };

  const cancel = () => {
    if (formHasChanges()) {
      setConfirmDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleSaveAndClose = () => {
    saveData();
  };

  const isFormValid = (): boolean => {
    return formState.isValid;
  };

  return (
    <RootDialog fullScreen open={open} onClose={handleClose}>
      <ClientDialogHeader
        passengerName={`${client?.legalName || ''}`}
        title={t('client.addServiceLevels')}
        steps={steps}
        activeStep={activeStep}
        isValid={true}
        step={handleStep}
        close={cancel}
        displayCompactStepper
      />
      <DialogContent className={classes.content} ref={dialogContentRef}>
        <Paper className={classes.contentPaper}>
          {activeStep > 0 && (
            <Box className={classes.previousButton} onClick={handlePrevious}>
              <ArrowBackIos sx={{ fontSize: 14 }} />
              {t('common.back')}
            </Box>
          )}
          <ServiceLevelsForm
            disableServiceLevelSelection={true}
            clientServiceLevels={clientServiceLevels}
            transportationAttributes={transportationAttributes}
            pricingModels={pricingModels}
            formHook={formHook}
          />
          <ClientOnboardingWizardFooter
            isLast={activeStep === steps.length - 1}
            isSaving={isSaving}
            isValid={isFormValid()}
            next={handleNext}
            saveAndClose={handleSaveAndClose}
          />
        </Paper>
      </DialogContent>
      <ConfirmationDialog
        title={t('client.confirmCancelTitle')}
        message={t('client.confirmCancelMessage')}
        open={confirmDialogOpen}
        positiveAction={handleClose}
        negativeAction={() => setConfirmDialogOpen(false)}
      />
    </RootDialog>
  );
}
